import React, { useCallback, useState } from "react";

import {
  Box,
  Container,
  Input,
  ListItem,
  Typography,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DeleteIcon from "@mui/icons-material/Delete";

import { formatEpisodeLengthToHumanFriendlyDuration } from "../../../../utils/formatEpisodeLengthToHumanFriendlyDuration";
import { formatFileSize } from "../../../../utils/formatFileSize";

interface UploadProps {
  setFiles: (files: File[]) => void;
  files: File[];
}

export default function Upload({ setFiles, files }: UploadProps) {
  const [fileDurationInSeconds, setFileDurationInSeconds] = useState<number>(0);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      // Create a media element to calculate the duration
      const mediaElement = file.type.startsWith("audio")
        ? new Audio(URL.createObjectURL(file))
        : document.createElement("video");

      mediaElement.src = URL.createObjectURL(file);

      mediaElement.addEventListener("loadedmetadata", () => {
        setFileDurationInSeconds(mediaElement.duration);
        setFiles(
          acceptedFiles.map((currentFile) =>
            Object.assign(currentFile, {
              duration_in_seconds: mediaElement.duration,
              preview: URL.createObjectURL(currentFile),
            }),
          ),
        );
      });
    },
    [setFiles],
  );

  const handleRemoveFile = () => {
    setFiles([]);
    setFileDurationInSeconds(0);
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: {
      "video/*": [],
      "audio/*": [],
    },
  });

  const filesList = files.map((file: Record<string, any>) => {
    return (
      <ListItem
        key={file.path}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
          position: "relative",
        }}
      >
        <CheckCircleIcon sx={{ color: "#22bb33", fontSize: 24 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          {file.type.startsWith("audio") ? (
            <AudioFileIcon sx={{ color: "#1976d2", fontSize: 20 }} />
          ) : (
            <VideoFileIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
          )}
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {file.path}
            </Typography>
            <Typography variant="caption" sx={{ color: "#666" }}>
              {formatFileSize(file.size)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Chip
            label={file.type.split("/")[1].toUpperCase()}
            size="small"
            sx={{
              backgroundColor: "#e3f2fd",
              color: "#1976d2",
              fontWeight: 600,
            }}
          />
        </Box>
        <IconButton
          onClick={handleRemoveFile}
          sx={{
            color: "#dc3545",
            backgroundColor: "#fff",
            border: "1px solid #dc3545",
            width: 32,
            height: 32,
            "&:hover": {
              backgroundColor: "#dc3545",
              color: "#fff",
            },
          }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </ListItem>
    );
  });

  return (
    <Container
      sx={{
        width: "100%",
        maxWidth: 600,
      }}
    >
      {!files.length ? (
        <Paper
          elevation={0}
          sx={{
            border: "2px dashed #1976d2",
            borderRadius: 4,
            p: 6,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: "#f8f9fa",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#e3f2fd",
              borderColor: "#1565c0",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
            },
          }}
          {...getRootProps({ className: "dropzone" })}
        >
          <Input inputProps={{ ...getInputProps() }} />
          <CloudUploadIcon
            sx={{
              fontSize: "64px",
              color: "#1976d2",
              mb: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: "#1976d2",
              fontWeight: 600,
            }}
          >
            Upload Your Episode
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              mb: 2,
            }}
          >
            Drag and drop your audio or video file here, or click to browse
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#999",
              fontSize: 14,
            }}
          >
            Supports MP3, WAV, MP4, MOV, and other common formats
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              textAlign: "center",
              color: "#23272E",
              fontWeight: 600,
            }}
          >
            File Selected
          </Typography>
          <Box sx={{ mb: 3 }}>{filesList}</Box>
          {fileDurationInSeconds ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Chip
                icon={<CheckCircleIcon />}
                label={`Duration: ${formatEpisodeLengthToHumanFriendlyDuration(fileDurationInSeconds)}`}
                color="success"
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                }}
              />
            </Box>
          ) : null}
          {fileRejections.length > 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#fff3e0",
                border: "1px solid #ffb74d",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ErrorOutlineIcon sx={{ color: "#f57c00" }} />
              <Typography
                variant="body2"
                sx={{ color: "#e65100", fontWeight: 500 }}
              >
                {fileRejections[0].errors[0].code === "too-many-files"
                  ? "You can upload only one file at a time."
                  : "Please select a valid audio or video file."}
              </Typography>
            </Paper>
          ) : null}
        </Box>
      )}
    </Container>
  );
}
